import React, { forwardRef } from "react";
import styled from "styled-components";
import { tokens } from "../../public/tokens";

const sizes = {
    xs: {
        padding: `0 ${tokens.globals["spacing-sm"]}`,
        fontSize: tokens.typography.fontSize["md-12px"],
        lineHeight: "16px",
        borderRadius: tokens.globals["border-radius"],
        height: tokens.globals["spacing-7"],
    },
    sm: {
        padding: `0 ${tokens.globals["spacing-2-5"]}`,
        fontSize: tokens.typography.fontSize["md-12px"],
        lineHeight: "16px",
        borderRadius: tokens.globals["border-radius"],
        height: tokens.globals["spacing-8"],
    },
    md: {
        padding: `0 ${tokens.globals["spacing-2-5"]}`,
        fontSize: tokens.typography.fontSize["lg-14px"],
        lineHeight: "20px",
        borderRadius: tokens.globals["border-radius"],
        height: tokens.globals["spacing-9"],
    },
    lg: {
        padding: `0 ${tokens.globals["spacing-2-5"]}`,
        fontSize: tokens.typography.fontSize["lg-14px"],
        lineHeight: "20px",
        borderRadius: tokens.globals["border-radius"],
        height: tokens.globals["spacing-10"],
    },
};

const StyledInputGroup = styled.div({
    width: "100%",
    display: "flex",
    flexDirection: "column",
    gap: tokens.globals["spacing-px"],
});

const StyledTop = styled.div({
    display: "flex",
    alignItems: "center",
    gap: tokens.globals["spacing-2"],
    minHeight: tokens.globals["spacing-5"],
});


const StyledLabel = styled.label({
    flex: "1 0 0",
    minWidth: 0,
    color: tokens.colors.text.primary,
    fontSize: tokens.typography.fontSize["lg-14px"],
    fontFamily: tokens.typography.fontFamilies["albert-sans"],
    fontWeight: tokens.typography.fontWeights.regular,
    lineHeight: "20px",
});

const StyledRequired = styled.span({
    color: tokens.colors.ui.destructive,
    fontSize: tokens.typography.fontSize["lg-14px"],
    lineHeight: "20px",
    marginLeft: tokens.globals["spacing-0-5"],
    position: "relative",
    top: tokens.globals["spacing-0-5"],
});

const StyledInfoIcon = styled.i({
    color: tokens.colors.text.secondary,
    fontSize: tokens.typography.fontSize["md-12px"],
    cursor: "help",
});

const StyledInput = styled.div(({ $size = "lg", $disabled = false, $error = false }) => {
    const config = sizes[$size] || sizes.lg;

    return {
        width: "100%",
        height: config.height,
        padding: config.padding,
        backgroundColor: $disabled ? tokens.colors.neutral.grey["100"] : tokens.colors.surfaces["container-background"],
        border: `${tokens.globals["spacing-px"]} solid ${$error ? tokens.colors.ui.destructive : tokens.colors.surfaces["border-divider"]}`,
        borderRadius: config.borderRadius,
        boxShadow: "none",
        display: "flex",
        alignItems: "center",
        opacity: $disabled ? 0.6 : 1,
        transition: "border-color 0.15s ease-in-out",

        "&:hover": $disabled ? {} : {
            borderColor: $error ? tokens.colors.ui.destructive : tokens.colors.neutral.grey["500"],
        },

        "&:focus-within": $disabled ? {} : {
            borderColor: $error ? tokens.colors.ui.destructive : tokens.colors.ui.ring,
        },
    };
});

const StyledInputField = styled.input(({ $size = "lg", disabled = false }) => {
    const config = sizes[$size] || sizes.lg;

    return {
        flex: 1,
        minWidth: 0,
        width: "100%",
        padding: 0,
        backgroundColor: "transparent",
        border: "none",
        outline: "none",
        color: disabled ? tokens.colors.text.disabled : tokens.colors.text.primary,
        fontFamily: tokens.typography.fontFamilies["albert-sans"],
        fontSize: config.fontSize,
        fontWeight: tokens.typography.fontWeights.regular,
        lineHeight: config.lineHeight,
        cursor: disabled ? "not-allowed" : "text",

        "&::placeholder": {
            color: tokens.colors.text.secondary,
            fontSize: config.fontSize,
            fontWeight: tokens.typography.fontWeights.regular,
        },

        "&::-webkit-outer-spin-button, &::-webkit-inner-spin-button": {
            WebkitAppearance: "none",
            margin: 0,
        },

        "&[type=number]": {
            MozAppearance: "textfield",
        },
    };
});

const StyledDivider = styled.span({
    width: tokens.globals["spacing-px"],
    height: tokens.globals["spacing-5"],
    marginLeft: tokens.globals["spacing-2"],
    background: tokens.colors.surfaces["border-divider"],
    flexShrink: 0,
});

const StyledControls = styled.span({
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    marginLeft: tokens.globals["spacing-1"],
    flexShrink: 0,
});

const StyledControlButton = styled.button({
    width: tokens.globals["spacing-5"],
    height: tokens.globals["spacing-4"],
    padding: 0,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    background: "transparent",
    border: 0,
    borderRadius: tokens.globals["border-radius"],
    color: tokens.colors.text.secondary,
    cursor: "pointer",
    fontSize: tokens.typography.fontSize["sm-10px"],

    "&:hover:not(:disabled)": {
        background: tokens.colors.brand.blue["50"],
        color: tokens.colors.brand.blue["500"],
    },

    "&:focus-visible": {
        outline: `${tokens.globals["spacing-px"]} solid ${tokens.colors.brand.blue["500"]}`,
        outlineOffset: tokens.globals["spacing-px"],
    },

    "&:disabled": {
        color: tokens.colors.text.disabled,
        cursor: "not-allowed",
        opacity: 0.6,
    },
});

const StyledMessage = styled.span(({ $error = false }) => ({
    color: $error ? tokens.colors.ui.destructive : tokens.colors.text.secondary,
    fontSize: tokens.typography.fontSize["md-12px"],
    fontFamily: tokens.typography.fontFamilies["albert-sans"],
    fontWeight: $error ? tokens.typography.fontWeights.medium : tokens.typography.fontWeights.regular,
    lineHeight: "16px",
}));

/**
 * Standard AIE numeric input with optional increment/decrement step controls,
 * sharing label/error/helper/disabled presentation with TextInput.
 * @param {(event: { target: { value: number, name: string } }) => void} props.onChange Value is a
 *   number (or "") whether triggered by typing or the step buttons — never a raw string.
 */
const NumberInput = forwardRef(function NumberInput(
    {
        placeholder = "Type here",
        value,
        onChange,
        size = "lg",
        disabled = false,
        disabledReason,
        error = false,
        label,
        required = false,
        info,
        helperText,
        errorText,
        min,
        max,
        step = 1,
        showControls = true,
        id,
        name,
        className,
        onBlur,
        onFocus,
        ...props
    },
    ref
) {
    const inputId = id || name || undefined;
    const messageId = inputId ? `${inputId}-message` : undefined;
    const showError = Boolean(error || errorText);
    const message = errorText || helperText;
    // Value from the input or a step button, falling back to 0 when it isn't a finite number.
    const parsedValue = typeof value === "number" ? value : Number.parseFloat(value);
    const numericValue = Number.isFinite(parsedValue) ? parsedValue : 0;
    const isAtMin = min !== undefined && numericValue <= min;
    const isAtMax = max !== undefined && numericValue >= max;

    const emitStepValue = (nextValue) => {
        if (!onChange) {
            return;
        }
        onChange({
            target: {
                value: nextValue,
                name: name || "",
            },
        });
    };

    const handleInputChange = (event) => {
        if (!onChange) {
            return;
        }
        const rawValue = event.target.value;
        if (rawValue === "") {
            emitStepValue("");
            return;
        }
        const parsed = Number.parseFloat(rawValue);
        if (!Number.isFinite(parsed)) {
            return;
        }
        const clampedMin = min !== undefined && min !== null ? Math.max(parsed, min) : parsed;
        const clamped = max !== undefined && max !== null ? Math.min(clampedMin, max) : clampedMin;
        emitStepValue(clamped);
    };

    const handleIncrement = () => {
        if (disabled || isAtMax) {
            return;
        }
        const nextValue = Math.min(max !== undefined ? max : Infinity, numericValue + step);
        emitStepValue(nextValue);
    };

    const handleDecrement = () => {
        if (disabled || isAtMin) {
            return;
        }
        const nextValue = Math.max(min !== undefined ? min : -Infinity, numericValue - step);
        emitStepValue(nextValue);
    };

    return (
        <StyledInputGroup className={className} title={disabled ? disabledReason : undefined}>
            {label && (
                <StyledTop>
                    <StyledLabel htmlFor={inputId}>
                        {label}
                        {required && <StyledRequired aria-hidden="true">*</StyledRequired>}
                    </StyledLabel>
                    {info && (
                        <StyledInfoIcon className="fal fa-info-circle" title={info} aria-label={info} />
                    )}
                </StyledTop>
            )}

            <StyledInput $size={size} $disabled={disabled} $error={showError}>
                <StyledInputField
                    {...props}
                    ref={ref}
                    id={inputId}
                    name={name}
                    type="number"
                    value={value}
                    onChange={handleInputChange}
                    onBlur={onBlur}
                    onFocus={onFocus}
                    disabled={disabled}
                    required={required}
                    placeholder={placeholder}
                    min={min}
                    max={max}
                    step={step}
                    $size={size}
                    aria-invalid={showError || undefined}
                    aria-required={required || undefined}
                    aria-describedby={message ? messageId : undefined}
                />

                {showControls && (
                    <>
                        <StyledDivider aria-hidden="true" />
                        <StyledControls>
                            <StyledControlButton
                                type="button"
                                onClick={handleIncrement}
                                disabled={disabled || isAtMax}
                                aria-label="Increment value"
                            >
                                <i className="fal fa-chevron-up" aria-hidden="true" />
                            </StyledControlButton>
                            <StyledControlButton
                                type="button"
                                onClick={handleDecrement}
                                disabled={disabled || isAtMin}
                                aria-label="Decrement value"
                            >
                                <i className="fal fa-chevron-down" aria-hidden="true" />
                            </StyledControlButton>
                        </StyledControls>
                    </>
                )}
            </StyledInput>

            {message && (
                <StyledMessage id={messageId} $error={Boolean(errorText)} role={errorText ? "alert" : undefined}>
                    {message}
                </StyledMessage>
            )}
        </StyledInputGroup>
    );
});

NumberInput.displayName = "NumberInput";

export default NumberInput;
